'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { useState, useCallback, Fragment, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import * as CustomPropTypes from '../../../../constants/PropTypes';
import ImmutablePropTypes from 'react-immutable-proptypes';
import Immutable from 'immutable';
import FormattedMessage from 'I18n/components/FormattedMessage';
import unescapedText from 'I18n/utils/unescapedText';
import UIFormControl from 'UIComponents/form/UIFormControl';
import UISearchInput from 'UIComponents/input/UISearchInput';
import { getHubSpotFeatureI18nKey } from 'ecosystem-marketplace-core/utils/i18n';
import { BY_PRODUCT as HUBSPOT_FEATURES_BY_PRODUCT } from 'ecosystem-marketplace-core/constants/groupTags/hubspotFeatures';
import NestedNav from 'ecosystem-marketplace-core/components/nav/NestedNav';
import NestedNavCheckboxItem from '../inputs/NestedNavCheckboxItem';
import UnstyledNestedNavItem from '../inputs/UnstyledNestedNavItem';

const getOpenIcon = open => open ? 'up' : 'down';

const hubspotFeaturesGroupsMap = Immutable.OrderedMap({
  crm: HUBSPOT_FEATURES_BY_PRODUCT.CRM,
  marketing: HUBSPOT_FEATURES_BY_PRODUCT.MARKETING,
  sales: HUBSPOT_FEATURES_BY_PRODUCT.SALES,
  service: HUBSPOT_FEATURES_BY_PRODUCT.SERVICE,
  cms: HUBSPOT_FEATURES_BY_PRODUCT.CMS,
  ops: HUBSPOT_FEATURES_BY_PRODUCT.OPS
}).map(groupFeatures => Immutable.Set(Object.values(groupFeatures)));

const HubSpotFeaturesGroup = (_ref) => {
  let {
    groupKey,
    groupFeatures,
    isMobile,
    onChange,
    searchTerm,
    selectedFeatures
  } = _ref,
      rest = _objectWithoutPropertiesLoose(_ref, ["groupKey", "groupFeatures", "isMobile", "onChange", "searchTerm", "selectedFeatures"]);

  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!isMobile) {
      setOpen(groupFeatures.some(feature => selectedFeatures.includes(feature)));
    }
  }, [groupFeatures, isMobile, selectedFeatures]);
  const renderedFeatures = useMemo(() => {
    return groupFeatures.map(feature => [feature, unescapedText(getHubSpotFeatureI18nKey(feature))]).filter(([, featureInputLabel]) => searchTerm ? featureInputLabel.toLowerCase().indexOf(searchTerm) !== -1 : true).sortBy(([, featureInputLabel]) => featureInputLabel).map(([feature, featureInputLabel]) => /*#__PURE__*/_jsx(NestedNavCheckboxItem, {
      checkboxProps: {
        checked: selectedFeatures.includes(feature),
        onChange: ({
          target: {
            checked
          }
        }) => {
          onChange(checked, [feature]);
        }
      },
      title: featureInputLabel
    }, feature));
  }, [groupFeatures, onChange, searchTerm, selectedFeatures]);

  if (renderedFeatures.isEmpty()) {
    return null;
  }

  return /*#__PURE__*/_jsx(UnstyledNestedNavItem, Object.assign({}, rest, {
    iconRight: searchTerm ? null : getOpenIcon(open),
    open: open || !!searchTerm,
    onOpenChange: ({
      target: {
        value
      }
    }) => {
      setOpen(value);
    },
    title: /*#__PURE__*/_jsx(FormattedMessage, {
      message: `appMarketplace.storefront.filters.hubspotFeatures.items.${groupKey}`
    }),
    children: renderedFeatures
  }));
};

const HubSpotFeaturesToggleGroup = ({
  isMobile,
  onChange,
  trackInteraction,
  value: selectedFeatures
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchTracked, setSearchTracked] = useState(false);
  const trackSearch = useCallback(() => {
    if (!searchTracked) {
      trackInteraction('search hubspot features');
      setSearchTracked(true);
    }
  }, [searchTracked, trackInteraction]);
  const handleChange = useCallback((checked, values) => {
    onChange(checked ? selectedFeatures.union(values) : selectedFeatures.subtract(values));
  }, [onChange, selectedFeatures]);
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [/*#__PURE__*/_jsx(UIFormControl, {
      children: /*#__PURE__*/_jsx(UISearchInput, {
        onBlur: () => {
          setSearchTracked(false);
        },
        onChange: ({
          target: {
            value
          }
        }) => {
          if (value.trim()) {
            setSearchTerm(value.toLowerCase());
            trackSearch();
          } else {
            setSearchTerm('');
          }
        },
        placeholder: unescapedText('appMarketplace.storefront.filters.hubspotFeatures.searchInputPlaceholder')
      })
    }), /*#__PURE__*/_jsx(UIFormControl, {
      children: /*#__PURE__*/_jsx(NestedNav, {
        className: "m-bottom-0",
        children: hubspotFeaturesGroupsMap.map((groupFeatures, groupKey) => {
          return /*#__PURE__*/_jsx(HubSpotFeaturesGroup, {
            isMobile: isMobile,
            groupKey: groupKey,
            groupFeatures: groupFeatures,
            onChange: handleChange,
            searchTerm: searchTerm,
            selectedFeatures: selectedFeatures
          }, groupKey);
        }).toArray()
      })
    })]
  });
};

HubSpotFeaturesToggleGroup.propTypes = {
  isMobile: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  trackInteraction: PropTypes.func.isRequired,
  value: ImmutablePropTypes.iterableOf(CustomPropTypes.hubspotFeature).isRequired
};
export default HubSpotFeaturesToggleGroup;